import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from "../../utils/colors";
import * as TypographyStyles from '../TypographyStyles';
import { TransferHistory } from './Types/transferHistory';
import { formatDuration } from '../../utils/durationUtils';
import { getColor } from '../../utils/colorFormatter';
import { NextButton, PrevButton } from '../Controls/PrevNextButtons';
import { slateColors } from '../ContentPages/AppLandingPage/styles';
import { useTranslation } from 'react-i18next';
import { TimeContainer } from '../Elements/TimeComponents';


const ContainerCard = styled('div')`
    display: flex;
    padding: 1.5rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 1.25rem;
    align-self: stretch;
    border-radius: 2.5rem;
    border: 1px solid ${slateColors[200]};
    background: white;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
    box-sizing: border-box;
    transition: all 0.3s;
    
    &:hover {
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        border-color: ${slateColors[300]};
    }
`

const CardHeader = styled('div')`
    display: flex;
    align-items: flex-start;
    gap: 4px;
`

const TableContainer = styled('div')`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    align-self: stretch;
`

const TableHeader = styled('div')`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    align-self: stretch;
`

const HeaderItem = styled(TypographyStyles.BodySRegular)<{ $minWidth?: string }>`
    display: flex;
    min-width: ${props => props.$minWidth ?? '80px'};
    align-items: flex-start;
    gap: 10px;
    color: ${slateColors[600]}
`

const TransferHistoryTable = styled('div')`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    align-self: stretch;
`

const DataRow = styled('div')`
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
`

const CellItem = styled(TypographyStyles.BodyMRegular)<{ $minWidth?: string }>`
    display: flex;
    min-width: ${props => props.$minWidth ?? '80px'};
    align-items: center;
    gap: 10px;
`

const Divider = styled.div`
  height: 1px;
  background-color: ${slateColors[200]};
  width: 100%;
  margin: 0;
`;

const CardFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    align-self: stretch;
`;

const PaginatorContainer = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
`;



interface TransferHistoryCardProps {
    transferHistoryData: TransferHistory[];
}

const formatDate = (dateString: string | null | undefined, t: any) => {
    if (!dateString) return '';
  
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
  
    const isToday = date.toDateString() === today.toDateString();
    const isYesterday = date.toDateString() === yesterday.toDateString();
  
    if (isToday) {
      return t('statistics.historyLabels.today');
    } else if (isYesterday) {
      return t('statistics.historyLabels.yesterday');
    } else {
      return date.toLocaleDateString('nl-BE', { month: '2-digit', day: '2-digit' });
    }
  };

const getDisplayInfo = (
    transferTime: number | null | undefined,
    arrivalCancelled: boolean | null,
    departureCancelled: boolean | null,
    t: any
) => {
    if (arrivalCancelled || departureCancelled || transferTime === null || transferTime === undefined) {
        return { formattedTransferTime: '-', displayColor: colors.grey600 };
    }
    if (transferTime < 1) {
        return { formattedTransferTime: t('statistics.historyLabels.missed'), displayColor: colors.red };
    }
    return { formattedTransferTime: null, displayColor: null };
};

const TIME_COLUMN_MIN_WIDTH = '120px';

const delaySecondsFromPlannedActual = (
    planned: string | null | undefined,
    actual: string | null | undefined
): number | undefined => {
    if (!planned || !actual) return undefined;
    const plannedMs = Date.parse(planned);
    const actualMs = Date.parse(actual);
    if (Number.isNaN(plannedMs) || Number.isNaN(actualMs)) return undefined;
    return (actualMs - plannedMs) / 1000;
};

const HistoricTransferTime: React.FC<{
    planned: string | null | undefined;
    actual: string | null | undefined;
    cancelled: boolean | null;
}> = ({ planned, actual, cancelled }) => {
    const { t } = useTranslation();

    if (cancelled) {
        if (!planned) {
            return (
                <TypographyStyles.BodyMRegular color={colors.red}>
                    {t('statistics.historyLabels.cancelled')}
                </TypographyStyles.BodyMRegular>
            );
        }
        return (
            <TimeContainer
                time={planned}
                delaySec={delaySecondsFromPlannedActual(planned, actual)}
                cancelled
                weight={400}
                size={14}
            />
        );
    }

    if (!planned || !actual) {
        return (
            <TypographyStyles.BodyMRegular color={colors.grey600}>
                {t('statistics.historyLabels.noData')}
            </TypographyStyles.BodyMRegular>
        );
    }

    return (
        <TimeContainer
            time={planned}
            delaySec={delaySecondsFromPlannedActual(planned, actual)}
            weight={400}
            size={14}
        />
    );
};

const PAGE_SIZE = 10;

const TransferHistoryCard: React.FC<TransferHistoryCardProps> = ({ transferHistoryData }) => {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    
    // Determine which date to use based on selectedOption
    const getDate = (entry: TransferHistory) => entry.plannedArrival;
    const getTransferTimeMinutes = (entry: TransferHistory) => entry.secondsToChange === null ? null : entry.secondsToChange / 60;

    // Sort the transferTime history data in reverse chronological order
    const sortedData = [...transferHistoryData].sort((a, b) => {
        const dateA = getDate(a) ? new Date(getDate(a)!) : new Date(0);
        const dateB = getDate(b) ? new Date(getDate(b)!) : new Date(0);
        return dateB.getTime() - dateA.getTime(); // Most recent dates first
    });

    // Pagination logic
    const totalPages = Math.ceil(sortedData.length / PAGE_SIZE);
    const paginatedData = sortedData.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    return (
        <ContainerCard>
            <CardHeader>
                <TypographyStyles.BodyLSemibold color={slateColors[900]}>{t('statistics.transferHistory')}</TypographyStyles.BodyLSemibold>
                <TypographyStyles.BodyLRegular color={slateColors[600]}>
                    {t('statistics.last30Days')}
                </TypographyStyles.BodyLRegular>
            </CardHeader>
            <TableContainer>
                <TableHeader>
                    <HeaderItem>{t('statistics.transferHistoryLabels.date')}</HeaderItem>
                    <HeaderItem $minWidth={TIME_COLUMN_MIN_WIDTH}>{t('statistics.transferHistoryLabels.arrival')}</HeaderItem>
                    <HeaderItem>{t('statistics.transferHistoryLabels.transferTime')}</HeaderItem>
                    <HeaderItem $minWidth={TIME_COLUMN_MIN_WIDTH}>{t('statistics.transferHistoryLabels.departure')}</HeaderItem>
                </TableHeader>
                <TransferHistoryTable>
                    {paginatedData.map((entry, index) => {
                        const arrivalCancelled = entry.arrivalCancelled;
                        const transferTime = getTransferTimeMinutes(entry);
                        const departureCancelled = entry.departureCancelled;

                        const { formattedTransferTime, displayColor } = getDisplayInfo(transferTime, arrivalCancelled, departureCancelled, t);

                        const displayTransferTime = formattedTransferTime || formatDuration(transferTime ?? 0);
                        const color = displayColor || (transferTime !== null && transferTime !== undefined ? getColor(transferTime, 'transferTimeInMinutes') : colors.grey600);
                        
                        return (
                        <React.Fragment key={index}>
                            <DataRow>
                                <CellItem color={slateColors[800]}>
                                        {formatDate(getDate(entry), t)}
                                </CellItem>
                                <CellItem $minWidth={TIME_COLUMN_MIN_WIDTH}>
                                    <HistoricTransferTime
                                        planned={entry.plannedArrival}
                                        actual={entry.actualArrivalTime}
                                        cancelled={arrivalCancelled}
                                    />
                                </CellItem>
                                <CellItem color={color}>
                                    {displayTransferTime}
                                </CellItem>
                                <CellItem $minWidth={TIME_COLUMN_MIN_WIDTH}>
                                    <HistoricTransferTime
                                        planned={entry.plannedDeparture}
                                        actual={entry.actualDepartureTime}
                                        cancelled={departureCancelled}
                                    />
                                </CellItem>
                            </DataRow>
                            {index < paginatedData.length - 1 && <Divider />}
                        </React.Fragment>
                        );
                    })}
                </TransferHistoryTable>
            </TableContainer>
            <CardFooter>
                <TypographyStyles.BodyMRegular color={slateColors[600]}>{t('statistics.historyLabels.page')} {currentPage} {t('statistics.historyLabels.of')} {totalPages}</TypographyStyles.BodyMRegular>
                <PaginatorContainer>
                    <PrevButton onClick={handlePrevPage} disabled={currentPage === 1} />
                    <NextButton onClick={handleNextPage} disabled={currentPage === totalPages} />
                </PaginatorContainer>
            </CardFooter>
        </ContainerCard>
    );
};


export default TransferHistoryCard;
